import React, { useState } from "react";
import { Grid, Typography, Box, Button, InputAdornment } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FormikProps } from "formik";
import { TalentProfile } from "../../../types/user";
import CustomTextField from "../../../components/common/CustomTextField";
import { Icons } from "../../../components/assets/icon";
import { StageCardFormValues } from "@/types/Card";

const GradientButton = styled(Button)(() => ({
  fontFamily: '"Lexend", sans-serif',
  background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
  color: "#fff",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "8px",
  padding: "12px 20px",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
    transform: "translateY(-2px)",
  },
}));

export const LabelBlock = ({
  label,
  required = false,
  helper,
}: {
  label: string;
  required?: boolean;
  helper?: string;
}) => (
  <Box>
    <Typography
      sx={{ font: "var(--semibold-md)", color: "var(--neutral-1000)", mb: 0.5 }}
    >
      {label} {required && <span style={{ color: "red" }}>*</span>}
    </Typography>
    {helper && (
      <Typography sx={{ font: "var(--body-sm)", color: "var(--neutral-500)" }}>
        {helper}
      </Typography>
    )}
  </Box>
);

type Props = {
  formik: FormikProps<StageCardFormValues>;
  currentProfile: TalentProfile;
  onNext: () => void;
  onBack: () => void;
};

export default function StepTwo_Pricing({ formik, onNext, onBack }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNext = async () => {
    setIsSubmitting(true);
    const errors = await formik.validateForm();

    const stepTwoFields = ["price", "duration", "date", "start_time"];

    const stepTwoErrors = Object.keys(errors).filter((key) =>
      stepTwoFields.includes(key)
    );

    if (stepTwoErrors.length === 0) {
      onNext();
    } else {
      const touchedFields = stepTwoFields.reduce(
        (acc, field) => {
          acc[field] = true;
          return acc;
        },
        {} as Record<string, boolean>
      );

      formik.setTouched(touchedFields, true);
      console.error("❌ Step 2 Validation Errors:", errors);
    }
    setIsSubmitting(false);
  };

  const chipText = {
    label: "Price",
    helper: "Specify the price for your services.",
  };

  return (
    <Box>
      <Typography
        variant="h5"
        fontWeight={700}
        textAlign="start"
        fontFamily="Lexend"
        mb={3}
        sx={{ color: "#333", pt: 3, pb: 2 }}
      >
        Scope & Pricing
      </Typography>

      <Grid container spacing={4}>
        {/* Price */}
        <Grid item xs={12} md={6}>
          <LabelBlock
            label={chipText.label}
            required
            helper={chipText.helper}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            {...formik.getFieldProps("price")}
            fullWidth
            type="number"
            value={formik.values.price}
            onChange={(e) => formik.setFieldValue("price", e.target.value)}
            errorMessage={formik?.touched?.price && formik?.errors?.price}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Icons.Coin />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>

        {/* Duration */}
        <Grid item xs={12} md={6}>
          <LabelBlock
            label="Duration (in mins)"
            helper="Specify the duration in Minutes."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fullWidth
            type="number"
            value={formik.values.duration}
            onChange={(e) => formik.setFieldValue("duration", e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      sx={{
                        font: "var(--semibold-md)",
                        color: "var(--neutral-1000)",
                      }}
                    >
                      min
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>

        {/* Navigation */}
      </Grid>

      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
          <GradientButton onClick={handleNext} disabled={isSubmitting}>
            Next
          </GradientButton>
        </Box>
      </Grid>
    </Box>
  );
}
